/**
 * Stencil <-> Redux connector, vendored from the archived `@stencil/redux` 0.2.0
 * package (https://github.com/ionic-team/stencil-redux).
 *
 * Kept in-repo so we can drop the Stencil collection dependency. That package's
 * `globalScript` has no default export, which Stencil 4 warns about on every build.
 *
 * MIT License
 *
 * Copyright (c) 2017 Ionic
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import type { Action as ReduxAction, AnyAction, Store as ReduxStore } from 'redux';

export type Unsubscribe = () => void;

export interface Store<S = any, A extends ReduxAction = AnyAction> {
  getState: () => S;
  getStore: () => ReduxStore<S, A>;
  setStore: (store: ReduxStore<S, A>) => void;
  mapStateToProps: <C extends R, R>(component: C, mapper: (state: S) => R) => Unsubscribe;
  mapDispatchToProps: <C extends { [K in keyof P]: any }, P>(component: C, props: P) => void;
}

export const store = ((): Store => {
  let reduxStore: ReduxStore;

  const setStore = (nextStore: ReduxStore) => {
    reduxStore = nextStore;
  };

  const getState = () => {
    return reduxStore && reduxStore.getState();
  };

  const getStore = () => {
    return reduxStore;
  };

  const mapDispatchToProps = (component: any, props: any) => {
    Object.keys(props).forEach((actionName) => {
      const action = props[actionName];
      Object.defineProperty(component, actionName, {
        get: () => (...args: any[]) => reduxStore.dispatch(action(...args)),
        configurable: true,
        enumerable: true
      });
    });
  };

  const mapStateToProps = (component: any, mapState: (...args: any[]) => any) => {
    const applyState = () => {
      const mergeProps = mapState(reduxStore.getState());
      Object.keys(mergeProps).forEach((newPropName) => {
        component[newPropName] = mergeProps[newPropName];
      });
    };

    const unsubscribe = reduxStore.subscribe(applyState);
    applyState();
    return unsubscribe;
  };

  return {
    getStore,
    setStore,
    getState,
    mapDispatchToProps,
    mapStateToProps
  };
})();
